import React, { useEffect } from 'react';
import { Input } from '@/shared/components/ui/Input';
import { FormField } from '@/shared/components/ui/FormField';
import { ConfigField } from '../types';

interface NormalizedField {
  key: string;
  type: 'string' | 'number' | 'boolean';
  required: boolean;
  description: string;
  defaultValue?: unknown;
  /** Rótulo em português vindo do provider. Sem ele, cai no nome do campo. */
  label?: string;
  enum?: string[]; // se presente → renderiza como <select> dropdown
  enumLabels?: Record<string, string>; // mapeia id → nome amigável (ex: 'deepseek-v4-pro' → 'DeepSeek V4-Pro')
}

interface DynamicConfigFormProps {
  schema: Record<string, ConfigField | unknown>;
  values: Record<string, unknown>;
  onChange: (field: string, value: unknown) => void;
}

/**
 * Normalizes inconsistent configSchema formats from providers.
 * Some use flat format: { fieldName: { type, required, description } }
 * Others use JSON Schema-like: { type: 'object', properties: { ... }, required: [...] }
 */
function normalizeSchema(schema: Record<string, unknown>): NormalizedField[] {
  // Detect JSON Schema format (has type: 'object' and properties)
  if (schema.type === 'object' && schema.properties && typeof schema.properties === 'object') {
    const properties = schema.properties as Record<string, Record<string, unknown>>;
    const requiredFields = Array.isArray(schema.required) ? (schema.required as string[]) : [];

    return Object.entries(properties).map(([key, prop]) => ({
      key,
      type: (prop.type as 'string' | 'number' | 'boolean') || 'string',
      required: requiredFields.includes(key),
      description: (prop.description as string) || key,
      ...(typeof prop.label === 'string' ? { label: prop.label } : {}),
      defaultValue: prop.default,
      ...(Array.isArray(prop.enum) ? { enum: prop.enum as string[] } : {}),
      ...(prop.enumLabels && typeof prop.enumLabels === 'object' ? { enumLabels: prop.enumLabels as Record<string, string> } : {}),
    }));
  }

  // Flat format: { fieldName: { type, required, description, enum?, enumLabels? } }
  return Object.entries(schema)
    .filter(([, value]) => value && typeof value === 'object')
    .map(([key, value]) => {
      const field = value as Record<string, unknown>;
      return {
        key,
        type: (field.type as 'string' | 'number' | 'boolean') || 'string',
        required: (field.required as boolean) ?? false,
        description: (field.description as string) || key,
        ...(typeof field.label === 'string' ? { label: field.label } : {}),
        defaultValue: field.default,
        ...(Array.isArray(field.enum) ? { enum: field.enum as string[] } : {}),
        ...(field.enumLabels && typeof field.enumLabels === 'object' ? { enumLabels: field.enumLabels as Record<string, string> } : {}),
      };
    });
}

/** Infer if a field should be masked as password */
function isSensitiveField(key: string): boolean {
  const sensitive = ['password', 'secret', 'token', 'apikey', 'privatekey', 'authtoken', 'accesstoken', 'refreshtoken'];
  const lower = key.toLowerCase();
  return sensitive.some(s => lower.includes(s));
}

/** Convert camelCase/snake_case to human-readable label */
function humanizeLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/^\w/, c => c.toUpperCase())
    .trim();
}

export const DynamicConfigForm: React.FC<DynamicConfigFormProps> = ({ schema, values, onChange }) => {
  const fields = normalizeSchema(schema as Record<string, unknown>);

  // Aplica defaultValue do schema no estado pai quando o campo está vazio.
  // Sem isso, o select renderiza visualmente com o default mas o estado pai
  // (`values`) fica `undefined`, e o submit envia config sem o campo —
  // o backend então rejeita com "Invalid provider configuration".
  useEffect(() => {
    fields.forEach((field) => {
      const current = values[field.key];
      const missing = current === undefined || current === null || current === '';
      if (missing && field.defaultValue !== undefined && field.defaultValue !== null && field.defaultValue !== '') {
        onChange(field.key, field.defaultValue);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só re-aplicar quando schema mudar (troca de provider)
  }, [schema]);

  if (fields.length === 0) {
    return (
      <p className="text-sm" style={{ color: '#9ca3af' }}>
        Este provider nao requer configuracao adicional.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {fields.map((field) => {
        const value = values[field.key] ?? field.defaultValue ?? '';

        if (field.type === 'boolean') {
          return (
            <label key={field.key} className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={Boolean(value)}
                onChange={(e) => onChange(field.key, e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <div>
                <span className="text-sm font-medium" style={{ color: '#1e293b' }}>
                  {humanizeLabel(field.key)}
                </span>
                {field.description && (
                  <p className="text-xs" style={{ color: '#9ca3af' }}>{field.description}</p>
                )}
              </div>
            </label>
          );
        }

        if (field.enum && field.enum.length > 0) {
          return (
            <FormField
              key={field.key}
              label={field.label ?? humanizeLabel(field.key)}
              description={field.description}
              required={field.required}
            >
              <select
                value={String(value)}
                onChange={(e) => onChange(field.key, e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-md border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                style={{ color: '#1e293b' }}
              >
                {!field.required && <option value="">— Selecione —</option>}
                {field.enum.map((opt) => (
                  <option key={opt} value={opt}>{field.enumLabels?.[opt] ?? opt}</option>
                ))}
              </select>
            </FormField>
          );
        }

        return (
          <FormField
            key={field.key}
            label={field.label ?? humanizeLabel(field.key)}
            description={field.description}
            required={field.required}
          >
            <Input
              type={field.type === 'number' ? 'number' : isSensitiveField(field.key) ? 'password' : 'text'}
              value={String(value)}
              onChange={(e) => {
                const val = field.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value;
                onChange(field.key, val);
              }}
              placeholder={field.description}
            />
          </FormField>
        );
      })}
    </div>
  );
};
